import {
  Address,
  Encounter,
  MedicalService,
  PreferredLanguageEnum,
  StateEnum,
  UserTypeEnum
} from "@vitable/asclepius-api-client-ts"
import { Ref, isReactive, isRef } from "vue"

export const hash: number = Math.floor(Math.random() * 90000) + 10000

export function getMemberLabel(member: any): string {
  return `${member.full_name}, ${member.date_of_birth}${member.company_name ? " — " + member.company_name : ""}`
}

export function getPointOfContactLabel(contact: any): string {
  return (
    `${contact.full_name && contact.full_name.length ? contact.full_name + ", " : ""}` +
    `${contact.phone ? contact.formatted_phone_number + ", " : ""}` +
    `${contact.email ? contact.email + ", " : ""}${contact.notes ? contact.notes : ""}`
  )
}

export function truncateString(string: string, maxLength: number): string {
  if (string.length <= maxLength) return string
  return `${string.slice(0, maxLength - 3)}....`
}

export function calculateAge(dob: string): number {
  const now: Date = new Date()
  const birthDate: Date = new Date(dob)
  let age: number = now.getFullYear() - birthDate.getFullYear()
  const m: number = now.getMonth() - birthDate.getMonth()
  if (m < 0) age--
  if (m === 0 && now.getDate() < birthDate.getDate()) age--
  return age
}

export function getCapitalizedWord(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

/**
 * Checks if a value is a plain object (not an array, null, or function).
 */
export function isObject(obj: any): boolean {
  return obj !== null && typeof obj === "object" && !Array.isArray(obj)
}

export function convertSnakecaseStringToCamelcase(str: string): string {
  return str.replace(/([-_][a-z])/gi, ($1) => {
    return $1.toUpperCase().replace("-", "").replace("_", "")
  })
}

export function convertSnakecaseObjectKeysToCamelcase(obj: any): any {
  let newObj: any = {}

  if (isObject(obj)) {
    Object.keys(obj).forEach((k) => {
      newObj[convertSnakecaseStringToCamelcase(k)] = convertSnakecaseObjectKeysToCamelcase(obj[k])
    })
  } else if (Array.isArray(obj)) {
    newObj = obj.map((i) => convertSnakecaseObjectKeysToCamelcase(i))
  } else {
    return obj
  }

  return newObj
}

export function generateRandomInt(max: number): number {
  return Math.floor(Math.random() * max)
}

export function addressToString(address: Address): string {
  let addressStr = `${address.address_line_1}`

  if (address.address_line_2) addressStr += ` ${address.address_line_2}`

  addressStr += ` ${address.city} ${address.state} ${address.zipcode}`
  return addressStr
}

export function addressFromString(addressStr: string): Address {
  const addressComponents = addressStr.split(", ")
  if (addressComponents.length === 5) {
    return {
      address_line_1: addressComponents[0],
      address_line_2: addressComponents[1],
      city: addressComponents[2],
      state: addressComponents[3] as StateEnum,
      zipcode: addressComponents[4]
    }
  }
  return {
    address_line_1: addressComponents[0],
    city: addressComponents[1],
    state: addressComponents[2] as StateEnum,
    zipcode: addressComponents[3]
  }
}

export function getReadablePreferredLanguageType(preferredLanguage: PreferredLanguageEnum): string {
  if (preferredLanguage === PreferredLanguageEnum.En) return "English"
  if (preferredLanguage === PreferredLanguageEnum.Es) return "Spanish"
  if (preferredLanguage === PreferredLanguageEnum.Zh) return "Chinese"
  if (preferredLanguage === PreferredLanguageEnum.Ru) return "Russian"
  if (preferredLanguage === PreferredLanguageEnum.Sw) return "Swahili"
  if (preferredLanguage === PreferredLanguageEnum.Th) return "Thai"
  return preferredLanguage
}

export function getReadableUserType(userType: UserTypeEnum): string {
  switch (userType) {
    case UserTypeEnum.Member:
      return "Patient"
    case UserTypeEnum.NursePractitioner:
      return "Provider"
    case UserTypeEnum.ClinicalAdmin:
      return "Support Team"
    default:
      return ""
  }
}

export function isMentalHealthEncounter(encounter: Encounter): boolean {
  return !!encounter.medical_services.filter((s: MedicalService) => s.name.toLowerCase().includes("mental health"))
    .length
}

/** @deprecated import { useStore } from "@/store" instead */
export { useStore } from "@/store"

/**
 * Determines if the given string is a valid UUID format.
 *
 * @param value the string value to be tested
 *
 * @returns true if valid; otherwise false
 */
export function isValidUUID(value: string | null | undefined): boolean {
  if (!value) {
    return false
  }
  return /^[-a-zA-Z0-9_]+$/.test(value)
}

/**
 * Determines if the given string is a valid email format.
 *
 * @param value the string value to be tested
 *
 * @returns true if valid; otherwise false
 */
export function isValidEmail(value: string | null | undefined): boolean {
  if (!value) {
    return false
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

/**
 * Determines if the given string is a valid phone number format.
 *
 * @param value the string value to be tested
 *
 * @returns true if valid; otherwise false
 */
export function isValidPhone(value: string | null | undefined): boolean {
  if (!value) {
    return false
  }
  return /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/.test(value)
}

/**
 * Resolves the value from a Ref or reactive object, or returns the value as is.
 *
 * @param src the source value (Ref, reactive object, or direct value)
 * @returns the resolved value
 */
export function resolveValue<T>(src: T | Ref<T>): T {
  return isRef(src) ? src.value : isReactive(src) ? src : src
}
